import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiPlus, FiTrash2, FiSearch, FiMapPin, FiExternalLink, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { buildQPublicUrl, hasQPublicUrl, getAvailableCounties } from '../utils/parcelUtils';
import { getAuthToken } from '../services/api';
import JobLocationPickerModal from '../components/JobLocationPickerModal';
import { parseCoordinateInput } from '../utils/coordinateUtils';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

function QuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(id);
  // Convert-from-quote-request: ?from_request=<id> tracks which PendingQuoteRequest we're converting
  const fromRequestId = searchParams.get('from_request');
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  
  const [formData, setFormData] = useState({
    client_id: '',
    bill_to_name: '',
    bill_to_company: '',
    bill_to_address: '',
    bill_to_city_state_zip: '',
    bill_to_email: '',
    bill_to_phone: '',
    quote_date: new Date().toLocaleDateString('en-CA'),
    valid_until: '',
    terms: '',
    po_number: '',
    notes: '',
    internal_notes: '',
    discount_percent: 0,
    job_ids: [],
    phase_id: '',
    line_items: [{ description: '', quantity: 1, rate: 0, job_id: null, is_required: false }],
    parcel_county: '',
    parcel_id: '',
    parcel_owner: '',
    parcel_address: '',
    parcel_acreage: '',
    parcel_land_lot: '',
    parcel_district: '',
    parcel_legal_description: '',
    parcel_deeds: '',
    parcel_plats: '',
    parcel_zoning: '',
    parcel_url: '',
    latitude: '',
    longitude: ''
  });
  
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [jobPhases, setJobPhases] = useState([]);   // phases of an added phased job
  const [showParcelSection, setShowParcelSection] = useState(false);
  const [showPinPicker, setShowPinPicker] = useState(false);

  // Site coordinates. A pasted "34.512345, -84.912345" (Google Maps' right-click copy)
  // fills both boxes; DMS like 34°30'15"N is converted when the box loses focus.
  const handleCoordPaste = (e) => {
    const text = e.clipboardData.getData('text');
    const pair = text.match(/(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)/);
    if (!pair) return;
    e.preventDefault();
    setFormData(prev => ({ ...prev, latitude: pair[1], longitude: pair[2] }));
  };
  const normalizeCoord = (field) => {
    const raw = String(formData[field] ?? '').trim();
    if (!raw) return;
    const { value, valid } = parseCoordinateInput(raw);
    if (!valid) return;
    let v = value;
    // Georgia is ~81-86 W; a longitude typed without its minus sign is always a slip.
    if (field === 'longitude' && v >= 80 && v <= 86) v = -v;
    const out = /^-?\d+(\.\d+)?$/.test(raw) && v === value ? raw : v.toFixed(6);
    if (out !== raw) setFormData(prev => ({ ...prev, [field]: out }));
  };
  const hasPin = formData.latitude !== '' && formData.longitude !== '' &&
    !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude));
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadClients();
    loadJobs();
    loadCompanySettings();
    if (isEditing) {
      loadQuote();
    } else {
      // Set default valid_until to 30 days from today
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + 30);
      setFormData(prev => ({ ...prev, valid_until: validUntil.toLocaleDateString('en-CA') }));

      // Prefill from quote-request URL params (when converting from a Pending Quote Request)
      const prefillName = searchParams.get('bill_to_name');
      const prefillEmail = searchParams.get('bill_to_email');
      const prefillPhone = searchParams.get('bill_to_phone');
      const prefillAddress = searchParams.get('parcel_address');
      const prefillNotes = searchParams.get('notes');
      if (prefillName || prefillEmail || prefillPhone || prefillAddress || prefillNotes) {
        setFormData(prev => ({
          ...prev,
          bill_to_name: prefillName || prev.bill_to_name,
          bill_to_email: prefillEmail || prev.bill_to_email,
          bill_to_phone: prefillPhone || prev.bill_to_phone,
          parcel_address: prefillAddress || prev.parcel_address,
          internal_notes: prefillNotes || prev.internal_notes,
        }));
      }
    }
  }, [id]);

  const loadClients = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/clients`, config);
      setClients(response.data.clients || response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadJobs = async () => {
    try {
      const config = await getAuthConfig();
      // Picker list only: lite omits the relationship walk that made this ~10 MB.
      const response = await axios.get(`${API_URL}/jobs?fields=lite`, config);
      setJobs(response.data.jobs || response.data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/quote-settings`, config);
      setCompanySettings(response.data);
      if (!isEditing && response.data.default_notes) {
        setFormData(prev => ({ ...prev, notes: response.data.default_notes }));
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
    }
  };

  const loadQuote = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/quotes/${id}`, config);
      const quote = response.data;
      
      setFormData({
        client_id: quote.client_id || '',
        bill_to_name: quote.bill_to_name || '',
        bill_to_company: quote.bill_to_company || '',
        bill_to_address: quote.bill_to_address || '',
        bill_to_city_state_zip: quote.bill_to_city_state_zip || '',
        bill_to_email: quote.bill_to_email || '',
        bill_to_phone: quote.bill_to_phone || '',
        quote_date: quote.quote_date || new Date().toLocaleDateString('en-CA'),
        valid_until: quote.valid_until || '',
        terms: quote.terms || '',
        po_number: quote.po_number || '',
        notes: quote.notes || '',
        internal_notes: quote.internal_notes || '',
        discount_percent: quote.discount_percent || 0,
        job_ids: quote.jobs?.map(j => j.id) || [],
        line_items: quote.line_items?.length > 0 
          ? quote.line_items.map(item => ({
              description: item.description,
              quantity: item.quantity,
              rate: item.rate,
              job_id: item.job_id,
              is_required: item.is_required || false
            }))
          : [{ description: '', quantity: 1, rate: 0, job_id: null, is_required: false }],
        parcel_county: quote.parcel_county || '',
        parcel_id: quote.parcel_id || '',
        parcel_owner: quote.parcel_owner || '',
        parcel_address: quote.parcel_address || '',
        parcel_acreage: quote.parcel_acreage || '',
        parcel_land_lot: quote.parcel_land_lot || '',
        parcel_district: quote.parcel_district || '',
        parcel_legal_description: quote.parcel_legal_description || '',
        parcel_deeds: quote.parcel_deeds || '',
        parcel_plats: quote.parcel_plats || '',
        parcel_zoning: quote.parcel_zoning || '',
        parcel_url: quote.parcel_url || '',
        latitude: quote.latitude ?? '',
        longitude: quote.longitude ?? ''
      });
      // Set client search term for the search input
      if (quote.client_id) {
        const client = clients.find(c => c.id === quote.client_id);
        if (client) {
          setClientSearchTerm(client.name + (client.company_name ? ` (${client.company_name})` : ''));
        } else {
          setClientSearchTerm(quote.bill_to_name || '');
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading quote:', error);
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    const cityStateZip = client ? [client.city, client.state, client.zip_code].filter(Boolean).join(', ') : '';
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      bill_to_name: client?.name || prev.bill_to_name || '',
      bill_to_company: client?.company_name || prev.bill_to_company || '',
      bill_to_address: client?.address || prev.bill_to_address || '',
      bill_to_city_state_zip: cityStateZip || prev.bill_to_city_state_zip || '',
      bill_to_email: client?.email || prev.bill_to_email || '',
      bill_to_phone: client?.phone || prev.bill_to_phone || ''
    }));
  };

  const handleAddJob = async (job) => {
    if (formData.job_ids.includes(job.id)) return;

    // The picker list is the lite payload, which deliberately omits the job's
    // billing_* overrides. Pull the full record so bill-to autofill still prefers
    // them; if that fails, fall back to the lite row rather than blocking the add.
    let full = job;
    try {
      const res = await axios.get(`${API_URL}/jobs/${job.id}`, await getAuthConfig());
      full = { ...job, ...res.data };
    } catch (e) {
      console.error('Could not load full job for billing autofill:', e);
    }

    // Add job ID
    const newJobIds = [...formData.job_ids, job.id];
    
    // Auto-fill billing info if empty
    const updates = { job_ids: newJobIds };
    if (!formData.bill_to_name) {
      updates.bill_to_name = full.billing_person || (full.client?.name) || '';
    }
    if (!formData.bill_to_company) {
      updates.bill_to_company = full.billing_company || (full.client?.company_name) || '';
    }
    if (!formData.bill_to_address) {
      updates.bill_to_address = full.billing_address || (full.client?.address) || '';
    }
    if (!formData.bill_to_city_state_zip) {
      const clientCSZ = full.client ? [full.client.city, full.client.state, full.client.zip_code].filter(Boolean).join(', ') : '';
      updates.bill_to_city_state_zip = full.billing_city_state_zip || clientCSZ || '';
    }
    if (!formData.client_id && full.client_id) {
      updates.client_id = full.client_id;
    }
    
    // Add a line item for the job if it has a description
    if (full.description_of_work) {
      const newLineItems = [...formData.line_items];
      // Replace empty first line item or add new one
      if (newLineItems.length === 1 && !newLineItems[0].description) {
        newLineItems[0] = { description: full.description_of_work, quantity: 1, rate: 0, job_id: job.id };
      } else {
        newLineItems.push({ description: full.description_of_work, quantity: 1, rate: 0, job_id: job.id });
      }
      updates.line_items = newLineItems;
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
    addTravelLine({ jobId: job.id, onlyIfMissing: true });

    // Phased job: load phases so the user can pick which client to bill; default to first.
    if (full.has_phases) {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/jobs/${job.id}/phases`, await getAuthConfig());
          const phases = res.data?.phases || [];
          setJobPhases(phases);
          if (phases.length) selectPhase(phases[0], phases);
        } catch (e) { /* leave as job client */ }
      })();
    } else {
      setJobPhases([]);
      setFormData(prev => ({ ...prev, phase_id: '' }));
    }
    setShowJobSelector(false);
    setJobSearchTerm('');
  };

  const selectPhase = (phase, phasesList) => {
    const list = phasesList || jobPhases;
    const p = typeof phase === 'object' ? phase : list.find(x => String(x.id) === String(phase));
    if (!p) { setFormData(prev => ({ ...prev, phase_id: '' })); return; }
    const csz = p.billing_city_state_zip || '';
    setFormData(prev => ({
      ...prev,
      phase_id: p.id,
      client_id: p.client_id || prev.client_id,
      bill_to_name: p.billing_person || prev.bill_to_name,
      bill_to_company: p.billing_company || prev.bill_to_company,
      bill_to_address: p.billing_address || prev.bill_to_address,
      bill_to_city_state_zip: csz || prev.bill_to_city_state_zip,
      bill_to_email: p.billing_email || prev.bill_to_email,
      bill_to_phone: p.billing_phone || prev.bill_to_phone,
    }));
  };

  const handleRemoveJob = (jobId) => {
    setFormData(prev => ({
      ...prev,
      job_ids: prev.job_ids.filter(id => id !== jobId)
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    
    // Auto-calculate amount
    if (field === 'quantity' || field === 'rate') {
      const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newLineItems[index].quantity) || 0;
      const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(newLineItems[index].rate) || 0;
      newLineItems[index].amount = qty * rate;
    }
    
    setFormData(prev => ({ ...prev, line_items: newLineItems }));
  };

  // Suggested travel line: round-trip drive time from the office x the travel rate in
  // Company Settings. Added automatically with the first job (never a second time - a
  // quote covering several nearby jobs is still one trip), or on demand via the button.
  const [travelNote, setTravelNote] = useState(null);
  const isTravelLine = (li) => /^travel\b/i.test(li.description || '');

  const addTravelLine = async ({ jobId = null, onlyIfMissing = false } = {}) => {
    if (onlyIfMissing && formData.line_items.some(isTravelLine)) return;
    const params = new URLSearchParams();
    const useJob = jobId || formData.job_ids[0];
    if (useJob) params.set('job_id', useJob);
    if (hasPin) { params.set('lat', formData.latitude); params.set('lng', formData.longitude); }
    if (formData.parcel_address) {
      const county = formData.parcel_county ? `, ${formData.parcel_county} County` : '';
      params.set('address', `${formData.parcel_address}${county}, GA`);
    }
    if (!params.toString()) {
      if (!onlyIfMissing) setTravelNote({ error: 'Add a job, site coordinates, or a parcel address first.' });
      return;
    }
    let data;
    try {
      data = (await axios.get(`${API_URL}/quotes/travel-estimate?${params}`, await getAuthConfig())).data;
    } catch (e) {
      if (!onlyIfMissing) setTravelNote({ error: 'Could not look up drive time.' });
      return;
    }
    setTravelNote({ data });
    if (!data.drive) {
      if (!onlyIfMissing) setTravelNote({ data, error: 'No location found for this job or address.' });
      return;
    }
    if (data.within_free_zone) return;   // the note under the lines says so
    // Without a rate the line would print as $0 travel on the client's quote - only
    // add it unasked when it's actually priced.
    if (onlyIfMissing && !data.rate) return;
    const rate = data.rate || 0;
    const line = {
      description: data.description, quantity: data.round_trip_hours, rate,
      amount: data.round_trip_hours * rate, job_id: useJob || null, is_required: false,
    };
    setFormData(prev => {
      if (onlyIfMissing && prev.line_items.some(isTravelLine)) return prev;
      return { ...prev, line_items: [...prev.line_items, line] };
    });
  };

  const handleAddLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, rate: 0, job_id: null, is_required: false }]
    }));
  };

  const handleRemoveLineItem = (index) => {
    if (formData.line_items.length === 1) return;
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => {
      const amount = parseFloat(item.quantity || 0) * parseFloat(item.rate || 0);
      return sum + amount;
    }, 0);
    const discountAmount = subtotal * (parseFloat(formData.discount_percent) || 0) / 100;
    const total = subtotal - discountAmount;
    return { subtotal, discountAmount, total };
  };

  const parseQPublicText = (text) => {
    const data = {};
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextLine = lines[i + 1] || '';
      const prevLine = i > 0 ? lines[i - 1] : '';

      // Owner — line after "Owner" header
      if (/^Owner$/i.test(line) && nextLine && !data.parcel_owner) {
        data.parcel_owner = nextLine;
      }

      // Parcel Number — tab-separated "Parcel Number\t078 053A"
      if (/^Parcel Number\t/i.test(line)) {
        data.parcel_id = line.split('\t').pop().trim();
      }

      // Location Address — tab-separated
      if (/^Location Address\t/i.test(line)) {
        data.parcel_address = line.split('\t').pop().trim();
      }

      // Legal Description — tab-separated
      if (/^Legal Description\t/i.test(line)) {
        data.parcel_legal_description = line.split('\t').pop().trim();
      }

      // Acres — tab-separated
      if (/^Acres\t/i.test(line)) {
        data.parcel_acreage = line.split('\t').pop().trim();
      }

      // Landlot/District — "95 / 6/3" format or tab-separated
      if (/^Landlot\/District\t/i.test(line)) {
        const val = line.split('\t').pop().trim();
        const parts = val.split('/').map(s => s.trim());
        if (parts.length >= 2) {
          data.parcel_land_lot = parts[0];
          data.parcel_district = parts.slice(1).join('/');
        }
      }

      // Tax District — extract district number
      if (/^Tax District\t/i.test(line) && !data.parcel_district) {
        const val = line.split('\t').pop().trim();
        const distMatch = val.match(/District\s*(\d+)/i);
        if (distMatch) data.parcel_district = distMatch[1];
      }

      // Sales section — extract deed book/page and plat book/page
      // Format: "3/29/2004	1060 22	42 282	$0	3	HALL JESSE LEE	BUNCH CHRISTINE E"
      // Or: "date\tDeedBook Page\tPlatBook Page\tprice\t..."
      if (/^Sales$/i.test(line)) {
        // Look at subsequent lines for sale records
        for (let j = i + 1; j < Math.min(i + 20, lines.length); j++) {
          const saleLine = lines[j];
          // Sale line starts with a date pattern (M/D/YYYY or MM/DD/YYYY)
          const saleMatch = saleLine.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\t(.+)/);
          if (saleMatch) {
            const parts = saleMatch[2].split('\t').map(s => s.trim());
            // parts[0] = deed book/page (e.g., "1060 22")
            // parts[1] = plat book/page (e.g., "42 282")
            if (parts[0] && /^\d/.test(parts[0])) {
              const deedParts = parts[0].split(/\s+/);
              if (deedParts.length >= 2) {
                const deedStr = `DB ${deedParts[0]}, PG ${deedParts[1]}`;
                data.parcel_deeds = data.parcel_deeds ? data.parcel_deeds + '; ' + deedStr : deedStr;
              }
            }
            if (parts[1] && /^\d/.test(parts[1])) {
              const platParts = parts[1].split(/\s+/);
              if (platParts.length >= 2) {
                const platStr = `PB ${platParts[0]}, PG ${platParts[1]}`;
                data.parcel_plats = data.parcel_plats ? data.parcel_plats + '; ' + platStr : platStr;
              }
            }
          }
          // Stop if we hit another section
          if (/^(Permits|Area Sales|View\/Pay|Historical)$/i.test(saleLine)) break;
        }
      }

      // Fallback: try tab-separated key\tvalue on any line
      if (line.includes('\t') && !data.parcel_id) {
        const parts = line.split('\t').map(p => p.trim());
        if (parts[0].toLowerCase() === 'parcel number') data.parcel_id = parts[1];
      }
    }

    // ---- Generic fallbacks (other counties, e.g. Cobb) — only fill what's still empty ----
    const LABELS = {
      parcel_id: ['Parcel Number', 'Parcel ID', 'Parcel No', 'Parcel #', 'Map/Parcel', 'Map Parcel', 'PIN', 'Parcel'],
      parcel_address: ['Location Address', 'Property Address', 'Physical Address', 'Site Address', 'Location'],
      parcel_legal_description: ['Legal Description', 'Brief Tax Description', 'Tax Description', 'Legal'],
      parcel_acreage: ['Acres', 'Acreage', 'Total Acres', 'Calculated Acres', 'Deeded Acres', 'Acres (Approx)'],
      parcel_land_lot: ['Land Lot', 'Landlot', 'Land Lot(s)'],
      parcel_district: ['Land District', 'District'],
      parcel_zoning: ['Zoning', 'Zoning Code', 'Current Zoning'],
      parcel_owner: ['Owner', 'Owner Name', 'Most Current Owner', 'Current Owner', 'Primary Owner'],
    };
    // Labels match with or without spaces — Cobb prints "ParcelNumber", "PropertyAddress"
    const squash = s => s.toLowerCase().replace(/[\s:]/g, '');
    const allLabels = Object.values(LABELS).flat().map(squash);
    const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const labelPattern = label => label.split(/\s+/).map(esc).join('\\s*');
    const isLabel = s => allLabels.includes(squash(s));
    const findValue = (labels) => {
      for (const label of labels) {
        const inline = new RegExp(`^${labelPattern(label)}\\s*(?::|\\t)\\s*(.+)$`, 'i');
        const bare = new RegExp(`^${labelPattern(label)}\\s*:?$`, 'i');
        for (let i = 0; i < lines.length; i++) {
          const m = lines[i].match(inline);
          if (m && m[1].trim()) return m[1].split('\t')[0].trim();
          if (bare.test(lines[i]) && lines[i + 1] && !isLabel(lines[i + 1])) return lines[i + 1].split('\t')[0].trim();
        }
      }
      return '';
    };
    for (const [field, labels] of Object.entries(LABELS)) {
      if (!data[field]) {
        const v = findValue(labels);
        if (v) data[field] = v;
      }
    }
    if (data.parcel_acreage) {
      const n = String(data.parcel_acreage).match(/[\d,]*\.?\d+/);
      if (n) data.parcel_acreage = n[0].replace(/,/g, '');
    }

    // Sales table with a header row (e.g. "Sale Date\tDeed Book\tDeed Page\tPlat Book\tPlat Page ...")
    if (!data.parcel_deeds && !data.parcel_plats) {
      const deeds = [], plats = [];
      for (let i = 0; i < lines.length; i++) {
        const header = lines[i].split('\t').map(h => h.trim().toLowerCase());
        if (header.length < 3 || !header.some(h => /book|deed/.test(h))) continue;
        const col = re => header.findIndex(h => re.test(h));
        const dBook = col(/deed.*book|^book$/), dPage = col(/deed.*page|^page$/), dBoth = col(/deed.*book.*page|^deed$|book\s*\/\s*page/);
        const pBook = col(/plat.*book/), pPage = col(/plat.*page/), pBoth = col(/plat.*book.*page|^plat$/);
        for (let j = i + 1; j < lines.length; j++) {
          const cells = lines[j].split('\t').map(c => c.trim());
          if (!/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(lines[j]) || cells.length < 3) break;
          const pick = (both, book, page) => {
            if (both >= 0 && both !== book && cells[both]) return cells[both].split(/[\s/-]+/).filter(Boolean);
            if (book >= 0 && page >= 0 && cells[book]) return [cells[book], cells[page]];
            if (book >= 0 && cells[book]) return cells[book].split(/[\s/-]+/).filter(Boolean);
            return null;
          };
          const d = pick(dBoth, dBook, dPage);
          if (d && d.length >= 2 && /\d/.test(d[0])) deeds.push(`DB ${d[0]}, PG ${d[1]}`);
          const p = pick(pBoth, pBook, pPage);
          if (p && p.length >= 2 && /\d/.test(p[0])) plats.push(`PB ${p[0]}, PG ${p[1]}`);
        }
        if (deeds.length || plats.length) break;
      }
      // Header-less sales rows under "Sales Information" (Cobb):
      // "9/2/2026\t$1,025,000\tWD\t16390\t1800\tVALID SALE\tGRANTOR\tGRANTEE" — book/page are
      // the first two consecutive all-numeric cells after the date
      if (!deeds.length) {
        const start = lines.findIndex(l => /^Sales(\s+Information)?$/i.test(l));
        for (let j = start + 1; start >= 0 && j < lines.length; j++) {
          if (!/^\d{1,2}\/\d{1,2}\/\d{4}\t/.test(lines[j])) {
            if (deeds.length || j > start + 3) break;
            continue;
          }
          const cells = lines[j].split('\t').map(c => c.trim());
          for (let k = 1; k < cells.length - 1; k++) {
            if (/^\d+[A-Z]?$/i.test(cells[k]) && /^\d+[A-Z]?$/i.test(cells[k + 1])) {
              deeds.push(`DB ${cells[k]}, PG ${cells[k + 1]}`);
              break;
            }
          }
        }
      }
      // Free text like "Deed Book 12345 Page 678" / "Plat Book 45 Page 12"
      if (!deeds.length) {
        for (const m of text.matchAll(/Deed\s*Book\s*:?\s*(\w+)\s*,?\s*(?:Page|Pg)\s*:?\s*(\w+)/gi)) deeds.push(`DB ${m[1]}, PG ${m[2]}`);
      }
      if (!plats.length) {
        for (const m of text.matchAll(/Plat\s*Book\s*:?\s*(\w+)\s*,?\s*(?:Page|Pg)\s*:?\s*(\w+)/gi)) plats.push(`PB ${m[1]}, PG ${m[2]}`);
      }
      if (deeds.length) data.parcel_deeds = [...new Set(deeds)].join('; ');
      if (plats.length) data.parcel_plats = [...new Set(plats)].join('; ');
    }

    // Cobb doesn't print land lot / district, but its parcel number encodes them:
    // 20034000990 → district 20, land lot 0340
    const isCobb = /cobb/i.test(formData.parcel_county || '') || /Cobb County/i.test(text);
    const cobbId = isCobb && String(data.parcel_id || '').replace(/\s/g, '').match(/^(\d{2})(\d{4})\d{5}$/);
    if (cobbId) {
      if (!data.parcel_district) data.parcel_district = String(parseInt(cobbId[1], 10));
      if (!data.parcel_land_lot) data.parcel_land_lot = String(parseInt(cobbId[2], 10));
    }

    return data;
  };

  const handlePasteImport = () => {
    if (!pasteText.trim()) return;
    const parsed = parseQPublicText(pasteText);
    setFormData(prev => ({
      ...prev,
      ...Object.fromEntries(Object.entries(parsed).filter(([_, v]) => v))
    }));
    setShowPasteModal(false);
    setPasteText('');
    const fieldCount = Object.keys(parsed).filter(k => parsed[k]).length;
    if (fieldCount > 0) {
      alert(`Imported ${fieldCount} field${fieldCount !== 1 ? 's' : ''} from qPublic data.`);
    } else {
      alert('Could not parse any parcel data from the pasted text. Try copying the entire parcel detail page from qPublic.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lat = formData.latitude === '' ? null : parseFloat(formData.latitude);
    const lng = formData.longitude === '' ? null : parseFloat(formData.longitude);
    if ((lat === null) !== (lng === null) || (lat !== null && (isNaN(lat) || isNaN(lng) ||
        Math.abs(lat) > 90 || Math.abs(lng) > 180))) {
      setShowParcelSection(true);
      alert('Site coordinates need both a valid latitude and longitude (e.g. 34.512345, -84.912345) — or leave both blank.');
      return;
    }
    setSaving(true);
    
    try {
      const config = await getAuthConfig();
      
      // Prepare line items with calculated amounts
      const lineItems = formData.line_items.map(item => ({
        ...item,
        amount: parseFloat(item.quantity || 0) * parseFloat(item.rate || 0)
      }));
      
      // Prepare jobs array
      const jobs = formData.job_ids.map(id => ({ id }));
      
      const payload = {
        ...formData,
        line_items: lineItems,
        jobs: jobs
      };
      
      let savedQuote;
      if (isEditing) {
        const resp = await axios.put(`${API_URL}/quotes/${id}`, payload, config);
        savedQuote = resp.data;
      } else {
        const resp = await axios.post(`${API_URL}/quotes`, payload, config);
        savedQuote = resp.data;
      }

      // If we converted from a Pending Quote Request, mark it as converted
      if (!isEditing && fromRequestId && savedQuote && savedQuote.id) {
        try {
          await axios.patch(`${API_URL}/quote-requests/${fromRequestId}`, {
            status: 'converted',
            converted_quote_id: savedQuote.id,
          }, config);
          navigate('/billing?tab=requests');
          return;
        } catch (e) {
          console.warn('Failed to flip quote request to converted:', e);
        }
      }

      navigate('/billing?tab=quotes');
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Failed to save quote');
      setSaving(false);
    }
  };

  const { subtotal, discountAmount, total } = calculateTotals();

  // Filter jobs for selector
  const filteredJobs = jobs.filter(job => {
    const searchLower = jobSearchTerm.toLowerCase();
    return (
      (job.job_number || '').toLowerCase().includes(searchLower) ||
      (job.property_address || '').toLowerCase().includes(searchLower) ||
      (job.client?.name || '').toLowerCase().includes(searchLower)
    );
  });

  // Get selected jobs
  const selectedJobs = jobs.filter(j => formData.job_ids.includes(j.id));

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading quote...</div>;
  }

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', maxWidth: '1000px', margin: '0 auto', overflowX: 'hidden' }}>
      <style>{`
        .quote-form input, .quote-form select, .quote-form textarea {
          box-sizing: border-box;
        }
      `}</style>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/billing?tab=quotes')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            marginBottom: '16px',
            padding: '0'
          }}
        >
          <FiArrowLeft /> Back to Quotes
        </button>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>
          {isEditing ? 'Edit Quote' : 'New Quote'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="quote-form">
        {/* Client & Bill To */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '16px' : '24px',
          marginBottom: '24px'
        }}>
          {/* Client Selection */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151' }}>
              Client
            </h2>
            <div style={{ position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 1 }} />
              <input
                type="text"
                value={clientSearchTerm}
                onChange={(e) => {
                  setClientSearchTerm(e.target.value);
                  setShowClientDropdown(true);
                }}
                onFocus={() => setShowClientDropdown(true)}
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                placeholder="Search clients..."
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              {formData.client_id && (
                <button
                  type="button"
                  onClick={() => {
                    setClientSearchTerm('');
                    setFormData(prev => ({
                      ...prev,
                      client_id: '',
                      bill_to_name: '',
                      bill_to_company: '',
                      bill_to_address: '',
                      bill_to_city_state_zip: '',
                      bill_to_email: '',
                      bill_to_phone: ''
                    }));
                  }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  Clear
                </button>
              )}
              {showClientDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  marginTop: '4px'
                }}>
                  {clients
                    .filter(c => {
                      if (!clientSearchTerm) return true;
                      const search = clientSearchTerm.toLowerCase();
                      return (c.name || '').toLowerCase().includes(search) ||
                        (c.company_name || '').toLowerCase().includes(search) ||
                        (c.email || '').toLowerCase().includes(search);
                    })
                    .slice(0, 50)
                    .map(client => (
                      <div
                        key={client.id}
                        onClick={() => {
                          handleClientChange(client.id);
                          setClientSearchTerm(client.name + (client.company_name ? ` (${client.company_name})` : ''));
                          setShowClientDropdown(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: formData.client_id === client.id.toString() ? '#eff6ff' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.client_id === client.id.toString() ? '#eff6ff' : 'transparent'}
                      >
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>{client.name}</div>
                        {client.company_name && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{client.company_name}</div>
                        )}
                        {client.email && (
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{client.email}</div>
                        )}
                      </div>
                    ))
                  }
                  {clients.filter(c => {
                    if (!clientSearchTerm) return true;
                    const search = clientSearchTerm.toLowerCase();
                    return (c.name || '').toLowerCase().includes(search) ||
                      (c.company_name || '').toLowerCase().includes(search) ||
                      (c.email || '').toLowerCase().includes(search);
                  }).length === 0 && (
                    <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                      No clients found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bill To */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151' }}>
              Quote To
            </h2>
            {jobPhases.length > 0 && (
              <div style={{ marginBottom: '14px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: '#3730a3' }}>
                  This job has phases — which one is this quote for?
                </label>
                <select
                  value={formData.phase_id || ''}
                  onChange={(e) => selectPhase(e.target.value)}
                  style={{ width: '100%', padding: '9px', borderRadius: '6px', border: '1px solid #c7d2fe', fontSize: '14px', background: 'white' }}
                >
                  <option value="">— Choose a phase —</option>
                  {jobPhases.map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {p.client_name || 'no client set'}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'grid', gap: '12px' }}>
              <input
                type="text"
                placeholder="Name"
                value={formData.bill_to_name}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_name: e.target.value }))}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
              />
              <input
                type="text"
                placeholder="Company"
                value={formData.bill_to_company}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_company: e.target.value }))}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
              />
              <input
                type="text"
                placeholder="Address"
                value={formData.bill_to_address}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_address: e.target.value }))}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
              />
              <input
                type="text"
                placeholder="City, State, ZIP"
                value={formData.bill_to_city_state_zip}
                onChange={(e) => setFormData(prev => ({ ...prev, bill_to_city_state_zip: e.target.value }))}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  width: '100%'
                }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.bill_to_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, bill_to_email: e.target.value }))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    width: '100%'
                  }}
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={formData.bill_to_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, bill_to_phone: e.target.value }))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    width: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quote Details */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>
            Quote Details
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '12px' : '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Quote Date
              </label>
              <input
                type="date"
                value={formData.quote_date}
                onChange={(e) => setFormData(prev => ({ ...prev, quote_date: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Valid Until
              </label>
              <input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Terms
              </label>
              <input
                type="text"
                placeholder="e.g., 50% deposit required"
                value={formData.terms}
                onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                PO Number
              </label>
              <input
                type="text"
                placeholder="Optional"
                value={formData.po_number}
                onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Related Jobs */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#374151' }}>
              Related Jobs
            </h2>
            <button
              type="button"
              onClick={() => setShowJobSelector(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <FiPlus size={16} /> Add Job
            </button>
          </div>
          
          {selectedJobs.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {selectedJobs.map(job => (
                <div
                  key={job.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <span style={{ fontWeight: '600', color: '#16a34a' }}>{job.job_number}</span>
                  <span style={{ color: '#6b7280' }}>- {job.property_address}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveJob(job.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '2px',
                      color: '#dc2626'
                    }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#9ca3af', margin: 0 }}>No jobs linked to this quote</p>
          )}
        </div>

        {/* Line Items */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>
            Line Items
          </h2>
          
          {isMobile ? (
            /* Mobile: card-based line items */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {formData.line_items.map((item, index) => (
                <div key={index} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginBottom: '8px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Qty</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'center',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Rate</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'right',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    {formData.line_items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#dc2626',
                          padding: '4px',
                          marginTop: '16px'
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!item.is_required}
                        onChange={(e) => handleLineItemChange(index, 'is_required', e.target.checked)}
                      />
                      Required (client can't decline)
                    </label>
                    <span style={{ fontWeight: '600', fontSize: '15px' }}>
                      {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop: table layout */
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', width: '100px' }}>Qty</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', width: '120px' }}>Rate</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', width: '120px' }}>Amount</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', width: '90px' }} title="If checked, the client cannot decline this item on the quote">Required</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.line_items.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="text"
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'right'
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0))}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!item.is_required}
                        onChange={(e) => handleLineItemChange(index, 'is_required', e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        title="Client cannot decline this item"
                      />
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {formData.line_items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#dc2626',
                            padding: '4px'
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          <button
            type="button"
            onClick={handleAddLineItem}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 16px',
              marginTop: '16px',
              backgroundColor: '#f3f4f6',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <FiPlus size={16} /> Add Line Item
          </button>
          <button
            type="button"
            onClick={() => addTravelLine()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 16px',
              marginTop: '8px',
              backgroundColor: '#f3f4f6',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <FiMapPin size={16} /> Add Travel
          </button>
          {travelNote && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: travelNote.error ? '#b45309' : '#6b7280' }}>
              {travelNote.error && <div>{travelNote.error}</div>}
              {travelNote.data?.drive && (
                <div>
                  Drive from office: {travelNote.data.drive.one_way_minutes} min each way ({travelNote.data.drive.one_way_miles} mi)
                  {travelNote.data.drive.approximate && ' - approximate, from the address'}
                  {travelNote.data.within_free_zone
                    ? ` - within the ${travelNote.data.free_minutes}-minute free zone, no travel charge`
                    : !travelNote.data.rate && ' - no travel rate available; set one in Company Settings'}
                </div>
              )}
              {travelNote.data?.field_hours?.median_hours != null && (
                <div>
                  Typical field time for {travelNote.data.field_hours.scope} jobs
                  {travelNote.data.field_hours.acreage_bucket ? ` (${travelNote.data.field_hours.acreage_bucket})` : ''}:
                  {' '}{travelNote.data.field_hours.median_hours} crew-hours (median of {travelNote.data.field_hours.sample_size})
                </div>
              )}
            </div>
          )}
          
          {/* Totals */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '2px solid #e5e7eb'
          }}>
            <div style={{ width: isMobile ? '100%' : '300px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span>Subtotal</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Discount</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: e.target.value }))}
                    style={{
                      width: '60px',
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      textAlign: 'right'
                    }}
                  />
                  <span>%</span>
                </div>
                <span style={{ color: '#dc2626' }}>-{formatCurrency(discountAmount)}</span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '12px 0',
                borderTop: '2px solid #333',
                marginTop: '8px'
              }}>
                <span style={{ fontSize: '18px', fontWeight: '700' }}>Total</span>
                <span style={{ fontSize: '18px', fontWeight: '700' }}>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Parcel / Property Information */}
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px', overflow: 'hidden'
        }}>
          <div
            onClick={() => setShowParcelSection(!showParcelSection)}
            style={{
              padding: '16px 20px', cursor: 'pointer', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
              backgroundColor: '#f9fafb', borderBottom: showParcelSection ? '1px solid #e5e7eb' : 'none'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiMapPin /> Property / Parcel Information
              {formData.parcel_id && <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '400' }}>({formData.parcel_id})</span>}
              {hasPin && <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: '400' }}>· pinned</span>}
            </h3>
            {showParcelSection ? <FiChevronDown /> : <FiChevronRight />}
          </div>

          {showParcelSection && (
            <div style={{ padding: '20px' }}>
              {/* Import buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                {formData.parcel_county && hasQPublicUrl(formData.parcel_county) && (
                  <a
                    href={buildQPublicUrl(formData.parcel_county)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '10px 16px', backgroundColor: '#2563eb', color: 'white',
                      borderRadius: '8px', textDecoration: 'none', fontSize: '13px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                  >
                    <FiExternalLink /> 1. Open qPublic
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setShowPasteModal(true)}
                  style={{
                    padding: '10px 16px', backgroundColor: '#16a34a', color: 'white',
                    border: 'none', borderRadius: '8px', fontSize: '13px',
                    fontWeight: '600', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', gap: '6px'
                  }}
                >
                  <FiMapPin /> {formData.parcel_county ? '2. Paste from qPublic' : 'Paste from qPublic'}
                </button>
              </div>

              {/* County */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '16px', alignItems: 'end' }}>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>County</label>
                  <input
                    type="text"
                    list="county-list"
                    value={formData.parcel_county}
                    onChange={(e) => setFormData(prev => ({ ...prev, parcel_county: e.target.value }))}
                    placeholder="e.g., Gordon"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                  <datalist id="county-list">
                    {getAvailableCounties().map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                {formData.parcel_county && hasQPublicUrl(formData.parcel_county) && (
                  <a
                    href={buildQPublicUrl(formData.parcel_county)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '10px 16px', backgroundColor: '#2563eb', color: 'white',
                      borderRadius: '8px', textDecoration: 'none', fontSize: '13px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FiExternalLink /> Open qPublic
                  </a>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Parcel Number</label>
                  <input type="text" value={formData.parcel_id} onChange={(e) => setFormData(prev => ({ ...prev, parcel_id: e.target.value }))}
                    placeholder="e.g., 064 026" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Acreage</label>
                  <input type="text" value={formData.parcel_acreage} onChange={(e) => setFormData(prev => ({ ...prev, parcel_acreage: e.target.value }))}
                    placeholder="e.g., 2.5" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Property Owner</label>
                <input type="text" value={formData.parcel_owner} onChange={(e) => setFormData(prev => ({ ...prev, parcel_owner: e.target.value }))}
                  placeholder="Owner name from qPublic" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Property Address</label>
                <input type="text" value={formData.parcel_address} onChange={(e) => setFormData(prev => ({ ...prev, parcel_address: e.target.value }))}
                  placeholder="Property address" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>
                  Site Coordinates <span style={{ fontWeight: '400', fontSize: '12px', color: '#6b7280' }}>— carried onto the job when the quote is approved</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input type="text" inputMode="decimal" value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                    onPaste={handleCoordPaste} onBlur={() => normalizeCoord('latitude')}
                    placeholder="Latitude (or paste lat, lng)"
                    style={{ flex: '1 1 160px', minWidth: 0, padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
                  <input type="text" inputMode="decimal" value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                    onPaste={handleCoordPaste} onBlur={() => normalizeCoord('longitude')}
                    placeholder="Longitude"
                    style={{ flex: '1 1 160px', minWidth: 0, padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
                  <button type="button" onClick={() => setShowPinPicker(true)}
                    style={{ padding: '10px 14px', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                      borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiMapPin /> {hasPin ? 'Adjust on Map' : 'Pick on Map'}
                  </button>
                  {hasPin && (
                    <a href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '13px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <FiExternalLink /> View
                    </a>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Land Lot</label>
                  <input type="text" value={formData.parcel_land_lot} onChange={(e) => setFormData(prev => ({ ...prev, parcel_land_lot: e.target.value }))}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>District</label>
                  <input type="text" value={formData.parcel_district} onChange={(e) => setFormData(prev => ({ ...prev, parcel_district: e.target.value }))}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Legal Description</label>
                <textarea value={formData.parcel_legal_description} onChange={(e) => setFormData(prev => ({ ...prev, parcel_legal_description: e.target.value }))}
                  rows={3} placeholder="Legal description from qPublic"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Deeds</label>
                  <textarea value={formData.parcel_deeds} onChange={(e) => setFormData(prev => ({ ...prev, parcel_deeds: e.target.value }))}
                    rows={2} placeholder="Deed book/page references"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Plats</label>
                  <textarea value={formData.parcel_plats} onChange={(e) => setFormData(prev => ({ ...prev, parcel_plats: e.target.value }))}
                    rows={2} placeholder="Plat book/page references"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box', resize: 'vertical' }} />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>Current Zoning</label>
                <input type="text" value={formData.parcel_zoning} onChange={(e) => setFormData(prev => ({ ...prev, parcel_zoning: e.target.value }))}
                  placeholder="e.g., R-1 Residential, A-1 Agricultural"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>

              <div>
                <label style={{ fontWeight: '500', fontSize: '14px', display: 'block', marginBottom: '6px' }}>qPublic URL</label>
                <input type="url" value={formData.parcel_url} onChange={(e) => setFormData(prev => ({ ...prev, parcel_url: e.target.value }))}
                  placeholder="Paste qPublic parcel URL here"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }} />
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '16px' : '24px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151' }}>
              Notes
            </h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Visible to client on the quote
            </p>
            <textarea
              placeholder="Any notes for the client..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151' }}>
              Internal Notes
            </h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Not visible to client
            </p>
            <textarea
              placeholder="Internal notes..."
              value={formData.internal_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/billing?tab=quotes')}
            style={{
              padding: '12px 24px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              background: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            <FiSave size={16} />
            {saving ? 'Saving...' : (isEditing ? 'Update Quote' : 'Create Quote')}
          </button>
        </div>
      </form>

      {showPinPicker && (
        <JobLocationPickerModal
          title="Set site location"
          job={{
            latitude: hasPin ? formData.latitude : null,
            longitude: hasPin ? formData.longitude : null,
            property_address: formData.parcel_address,
            county: formData.parcel_county,
          }}
          onPick={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))}
          onClose={() => setShowPinPicker(false)}
        />
      )}

      {/* Job Selector Modal */}
      {showJobSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ margin: '0 0 16px', color: '#1f2937' }}>Select Job</h3>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Search jobs..."
                value={jobSearchTerm}
                onChange={(e) => setJobSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 38px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                autoFocus
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
              {filteredJobs.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>No jobs found</p>
              ) : (
                filteredJobs.slice(0, 20).map(job => (
                  <div
                    key={job.id}
                    onClick={() => handleAddJob(job)}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      backgroundColor: formData.job_ids.includes(job.id) ? '#ecfdf5' : 'transparent'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#16a34a' }}>{job.job_number}</div>
                    <div style={{ fontSize: '14px', color: '#374151' }}>{job.property_address}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{job.client?.name}</div>
                  </div>
                ))
              )}
            </div>
            <button
              onClick={() => { setShowJobSelector(false); setJobSearchTerm(''); }}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Paste from qPublic Modal */}
      {showPasteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}><FiMapPin /> Import from qPublic</h3>
            <div style={{
              fontSize: '13px', color: '#6b7280', marginBottom: '16px',
              backgroundColor: '#eff6ff', padding: '12px', borderRadius: '8px'
            }}>
              <strong>Instructions:</strong>
              <ol style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                <li>Open the parcel on qPublic (click "Open qPublic" button above)</li>
                <li>On the parcel detail page, select all text (Ctrl+A / Cmd+A)</li>
                <li>Copy it (Ctrl+C / Cmd+C)</li>
                <li>Paste it in the box below (Ctrl+V / Cmd+V)</li>
              </ol>
            </div>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste the qPublic parcel page content here..."
              rows={12}
              style={{
                width: '100%', padding: '12px', border: '1px solid #ddd',
                borderRadius: '8px', fontSize: '13px', fontFamily: 'monospace',
                resize: 'vertical', boxSizing: 'border-box', marginBottom: '16px'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { setShowPasteModal(false); setPasteText(''); }}
                style={{
                  padding: '10px 20px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePasteImport}
                disabled={!pasteText.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: pasteText.trim() ? '#16a34a' : '#ccc',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: pasteText.trim() ? 'pointer' : 'not-allowed', fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Import Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuoteForm;